// Offline (broker-less) WebRTC signalling.
//
// Why this exists: the PeerJS path needs a public broker
// (0.peerjs.com) to swap offer/answer + ICE candidates before two
// browsers can open a DataChannel. On a LAN with no internet, the
// broker is unreachable and the app can't start a session.
//
// Manual signalling fixes that — each device prints a QR the other
// scans. After that one exchange, the rest is pure peer-to-peer
// WebRTC.
//
// **Size matters.** A full Chrome/Safari SDP is 1.5–2 KB; even
// lz-string-compressed it produces a dense version-35+ QR that
// phone cameras can't reliably lock onto. So instead of round-
// tripping the whole SDP, we extract just the four fields that
// actually vary per session:
//
//     ufrag        ~4 bytes
//     pwd          ~22 bytes
//     fingerprint  ~95 bytes (sha-256 hex with colons)
//     candidates   one or more "candidate:..." lines
//
// Everything else in a DataChannel-only SDP is boilerplate, so we
// rebuild it from a fixed template on the receiving side. The
// resulting QR payload is ~250–500 bytes — easy version-15–20 QRs.
//
// Wire format:
//     "MS" <ver> <role> <lz-string compressed JSON>
// where the JSON is `[ufrag, pwd, fp, [candidates...]]`. Role is
// 'O' (offer) or 'A' (answer); the matching `a=setup:` line is
// derived from role at SDP-rebuild time.

import LZString from 'lz-string'

export type Role = 'offer' | 'answer'

const PROTO_VERSION = '2'

interface SignalingPayload {
  role: Role
  sdp: string
}

interface CompactSignal {
  ufrag: string
  pwd: string
  fp: string                // sha-256, with colons, exactly as in SDP
  candidates: string[]      // "candidate:..." lines, no "a=" prefix
}

/** Optional candidate fields that don't affect ICE matching; stripping
 *  them shaves ~30% off each candidate line. ICE only requires
 *  foundation, component, transport, priority, address, port, "typ",
 *  and candidate-type. Everything after that is informational. */
function trimCandidate(line: string): string {
  // Drop "generation N", "network-cost N", "network-id N", "ufrag X".
  // Each appears at most once per candidate; order varies.
  return line
    .replace(/ generation \d+/g, '')
    .replace(/ network-cost \d+/g, '')
    .replace(/ network-id \d+/g, '')
    .replace(/ ufrag \S+/g, '')
    .trim()
}

/** Extract the essential fields from a full SDP. */
function parseSdp(sdp: string): CompactSignal {
  let ufrag = ''
  let pwd = ''
  let fp = ''
  const candidates: string[] = []
  for (const line of sdp.split(/\r?\n/)) {
    if (line.startsWith('a=ice-ufrag:')) ufrag = line.slice('a=ice-ufrag:'.length).trim()
    else if (line.startsWith('a=ice-pwd:')) pwd = line.slice('a=ice-pwd:'.length).trim()
    else if (line.startsWith('a=fingerprint:sha-256 ')) {
      fp = line.slice('a=fingerprint:sha-256 '.length).trim()
    } else if (line.startsWith('a=candidate:')) {
      candidates.push(trimCandidate(line.slice(2))) // strip "a="
    }
  }
  if (!ufrag || !pwd || !fp) {
    throw new Error('SDP missing ICE / fingerprint fields — cannot pair.')
  }
  return { ufrag, pwd, fp, candidates }
}

/**
 * Rebuild a complete DataChannel-only SDP from a compact signal. The
 * boilerplate matches what Chrome / Safari / Firefox produce for a
 * single ordered DataChannel — both sides accept it as a valid
 * remote description.
 */
function buildSdp(role: Role, sig: CompactSignal): string {
  // For an offer the DTLS role is "actpass" (let the other side choose);
  // for an answer we commit to "active". This is the standard pattern
  // and what every browser produces for DataChannel sessions.
  const setup = role === 'offer' ? 'actpass' : 'active'
  // session-id just has to be a positive integer that both sides see
  // consistently. We use a fresh one each rebuild — the remote side
  // doesn't care.
  const sessId = Math.floor(Math.random() * 0xffffffff)
  const lines = [
    'v=0',
    `o=- ${sessId} 2 IN IP4 127.0.0.1`,
    's=-',
    't=0 0',
    'a=group:BUNDLE 0',
    'a=msid-semantic: WMS',
    'm=application 9 UDP/DTLS/SCTP webrtc-datachannel',
    'c=IN IP4 0.0.0.0',
    'a=ice-options:trickle',
    `a=ice-ufrag:${sig.ufrag}`,
    `a=ice-pwd:${sig.pwd}`,
    `a=fingerprint:sha-256 ${sig.fp}`,
    `a=setup:${setup}`,
    'a=mid:0',
    'a=sctp-port:5000',
    'a=max-message-size:262144',
    ...sig.candidates.map((c) => `a=${c}`),
    'a=end-of-candidates',
  ]
  return lines.join('\r\n') + '\r\n'
}

/** Encode an SDP into a compact, QR-friendly string. */
export function encodeSignal(role: Role, sdp: string): string {
  const sig = parseSdp(sdp)
  const json = JSON.stringify([sig.ufrag, sig.pwd, sig.fp, sig.candidates])
  const prefix = role === 'offer' ? 'O' : 'A'
  return `MS${PROTO_VERSION}${prefix}${LZString.compressToEncodedURIComponent(json)}`
}

/** Reverse of `encodeSignal`. Throws on malformed input. */
export function decodeSignal(payload: string): SignalingPayload {
  const trimmed = payload.trim()
  if (!trimmed.startsWith('MS')) {
    throw new Error("That QR isn't a Multispeaker pairing code.")
  }
  if (trimmed[2] !== PROTO_VERSION) {
    throw new Error(
      `Unsupported pairing-code version "${trimmed[2]}". Update both apps so they match.`,
    )
  }
  const roleChar = trimmed[3]
  const role: Role | null = roleChar === 'O' ? 'offer' : roleChar === 'A' ? 'answer' : null
  if (!role) throw new Error('Malformed pairing code (bad role marker).')
  const decoded = LZString.decompressFromEncodedURIComponent(trimmed.slice(4))
  if (!decoded) throw new Error("Couldn't decompress that pairing code — try regenerating it.")
  let parsed: unknown
  try {
    parsed = JSON.parse(decoded)
  } catch {
    throw new Error('Malformed pairing code (bad JSON).')
  }
  if (!Array.isArray(parsed) || parsed.length < 3) {
    throw new Error('Malformed pairing code (bad shape).')
  }
  const [ufrag, pwd, fp, candidates] = parsed as [string, string, string, string[] | undefined]
  const sdp = buildSdp(role, {
    ufrag,
    pwd,
    fp,
    candidates: Array.isArray(candidates) ? candidates : [],
  })
  return { role, sdp }
}

/**
 * Build the WebRTC `RTCConfiguration` used for offline pairing. No
 * STUN / TURN — relies on host (LAN) candidates that browsers gather
 * locally. Two devices on the same Wi-Fi can always reach each other
 * via .local mDNS candidates without phoning home.
 */
function offlineRtcConfig(): RTCConfiguration {
  return { iceServers: [] }
}

/** Wait until ICE gathering completes, then return the final SDP. */
async function waitForIceGathering(pc: RTCPeerConnection): Promise<string> {
  if (pc.iceGatheringState === 'complete') {
    return pc.localDescription!.sdp
  }
  return new Promise<string>((resolve) => {
    const check = () => {
      if (pc.iceGatheringState === 'complete') {
        pc.removeEventListener('icegatheringstatechange', check)
        resolve(pc.localDescription!.sdp)
      }
    }
    pc.addEventListener('icegatheringstatechange', check)
    // Hard cap — some browsers never fire 'complete' (Safari has
    // historically been flaky here). Take whatever we have after 3 s.
    setTimeout(() => {
      pc.removeEventListener('icegatheringstatechange', check)
      resolve(pc.localDescription!.sdp)
    }, 3_000)
  })
}

/** Wire up basic connection-state logging so we can diagnose the
 *  next hang (ICE failed, DTLS rejected, etc.) without adding new
 *  instrumentation. */
function attachConnectionLogging(pc: RTCPeerConnection, label: string) {
  pc.addEventListener('iceconnectionstatechange', () => {
    // eslint-disable-next-line no-console
    console.info(`[Multispeaker ${label}] iceConnectionState=${pc.iceConnectionState}`)
  })
  pc.addEventListener('connectionstatechange', () => {
    // eslint-disable-next-line no-console
    console.info(`[Multispeaker ${label}] connectionState=${pc.connectionState}`)
  })
}

export interface OfferHandle {
  pc: RTCPeerConnection
  dc: RTCDataChannel
  /** Encoded QR-friendly signaling string. */
  offer: string
  /** Apply the joiner's answer once it's scanned in. */
  acceptAnswer: (encodedAnswer: string) => Promise<void>
  /** Resolved when the DataChannel is open and ready. */
  connected: Promise<void>
}

/**
 * Host-side: create a DataChannel, generate an offer, wait for ICE
 * gathering, and return everything the UI needs to display the QR
 * and later accept the answer.
 */
export async function createOffer(): Promise<OfferHandle> {
  const pc = new RTCPeerConnection(offlineRtcConfig())
  attachConnectionLogging(pc, 'host pc')
  const dc = pc.createDataChannel('multispeaker', { ordered: true })
  dc.binaryType = 'arraybuffer'

  const offerInit = await pc.createOffer()
  await pc.setLocalDescription(offerInit)
  const finalSdp = await waitForIceGathering(pc)
  const encoded = encodeSignal('offer', finalSdp)

  const connected = new Promise<void>((resolve, reject) => {
    if (dc.readyState === 'open') {
      resolve()
      return
    }
    const onOpen = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(new Error('DataChannel error before opening.'))
    }
    const onStateChange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        cleanup()
        reject(new Error(`peer connection ${pc.connectionState}`))
      }
    }
    const cleanup = () => {
      dc.removeEventListener('open', onOpen)
      dc.removeEventListener('error', onError)
      pc.removeEventListener('connectionstatechange', onStateChange)
    }
    dc.addEventListener('open', onOpen)
    dc.addEventListener('error', onError)
    pc.addEventListener('connectionstatechange', onStateChange)
  })

  const acceptAnswer = async (encodedAnswer: string) => {
    const { role, sdp } = decodeSignal(encodedAnswer)
    if (role !== 'answer') {
      throw new Error(`Expected an answer code, got a ${role}.`)
    }
    await pc.setRemoteDescription({ type: 'answer', sdp })
  }

  return { pc, dc, offer: encoded, acceptAnswer, connected }
}

export interface AnswerHandle {
  pc: RTCPeerConnection
  /** The DataChannel arrives via the PC's `datachannel` event, which
   *  fires when the host's channel propagates over to our side. On
   *  Chrome this is moments after setRemoteDescription; on Firefox /
   *  Safari it can wait for the full DTLS+SCTP handshake — i.e., after
   *  the host scans our answer back. The caller awaits this promise
   *  once it's done showing the answer QR. */
  dcReady: Promise<RTCDataChannel>
  /** Encoded QR-friendly answer to display back to the host. */
  answer: string
  /** Resolved when the DataChannel is open. Built on top of dcReady. */
  connected: Promise<void>
}

/**
 * Joiner-side: consume the host's offer code, build an answer, and
 * return immediately so the caller can display the answer QR. The
 * DataChannel arrives later via `dcReady` — DO NOT await it inside
 * this function or you'll deadlock the pairing flow (the host can't
 * complete the handshake until it scans the answer the caller is
 * waiting to display).
 */
export async function acceptOffer(encodedOffer: string): Promise<AnswerHandle> {
  const { role, sdp } = decodeSignal(encodedOffer)
  if (role !== 'offer') {
    throw new Error(`Expected an offer code, got a ${role}.`)
  }

  const pc = new RTCPeerConnection(offlineRtcConfig())
  attachConnectionLogging(pc, 'join pc')

  // Set up the 'datachannel' listener BEFORE setRemoteDescription so
  // we never miss the event regardless of which browser-specific
  // timing path we hit.
  const dcReady = new Promise<RTCDataChannel>((resolve) => {
    pc.addEventListener('datachannel', (ev) => {
      // eslint-disable-next-line no-console
      console.info('[Multispeaker join pc] datachannel event fired')
      ev.channel.binaryType = 'arraybuffer'
      resolve(ev.channel)
    })
  })

  await pc.setRemoteDescription({ type: 'offer', sdp })
  const answerInit = await pc.createAnswer()
  await pc.setLocalDescription(answerInit)
  const finalSdp = await waitForIceGathering(pc)
  const encoded = encodeSignal('answer', finalSdp)

  const connected = dcReady.then((dc) =>
    new Promise<void>((resolve, reject) => {
      if (dc.readyState === 'open') {
        resolve()
        return
      }
      dc.addEventListener('open', () => resolve(), { once: true })
      dc.addEventListener('error', () => reject(new Error('DataChannel error.')), { once: true })
    }),
  )

  return { pc, dcReady, answer: encoded, connected }
}
